import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, signup as apiSignup, verifySignup as apiVerifySignup, googleAuth as apiGoogleAuth, adminLogin as apiAdminLogin, getMe } from '../api/auth.api';
import { celebrate } from '../components/brand/celebrate';

const AuthContext = createContext(null);

/* ── Storage keys ───────────────────────────────────────────── */
const USER_KEY        = 'ks_user';
const TOKEN_KEY       = 'ks_token';
const ADMIN_TOKEN_KEY = 'ks_admin_token';

export function AuthProvider({ children }) {
  // ── State ──────────────────────────────────────────────────
  const [user,         setUser]         = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); }
    catch { return null; }
  });
  const [isLoggedIn,   setIsLoggedIn]   = useState(() => !!localStorage.getItem(TOKEN_KEY));
  const [isAdmin,      setIsAdmin]      = useState(() => !!localStorage.getItem(ADMIN_TOKEN_KEY));
  const [authLoading,  setAuthLoading]  = useState(false);
  const [authError,    setAuthError]    = useState('');

  // ── On mount: validate stored token ────────────────────────
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    // Silently verify the token is still valid
    getMe()
      .then(({ user: me }) => {
        setUser(me);
        setIsLoggedIn(true);
        localStorage.setItem(USER_KEY, JSON.stringify(me));
      })
      .catch(() => {
        // Token invalid or expired — clear everything
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        setIsLoggedIn(false);
      });
  }, []);

  // ── User login ──────────────────────────────────────────────
  const login = useCallback(async ({ email, password }) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const { token, user: me } = await apiLogin({ email, password });
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(me));
      setUser(me);
      setIsLoggedIn(true);
      /* The sidebar does not exist yet — the bus parks this until the
         shell mounts after the redirect. */
      celebrate();
      return { success: true };
    } catch (err) {
      setAuthError(err.message);
      return { success: false, error: err.message };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  /* ── User signup, step 1 ─────────────────────────────────────
     Does NOT log anyone in. The account does not exist yet — this
     only sends the code. verifySignup below is what creates it. */
  const signup = useCallback(async ({ name, email, password }) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await apiSignup({ name, email, password });
      return { success: true, data: res.data };
    } catch (err) {
      setAuthError(err.message);
      return { success: false, error: err.message };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // ── User signup, step 2 — the code checks out, so log them in ──
  const verifySignup = useCallback(async ({ email, otp }) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const { token, user: me } = await apiVerifySignup({ email, otp });
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(me));
      setUser(me);
      setIsLoggedIn(true);
      /* The sidebar does not exist yet — the bus parks this until the
         shell mounts after the redirect. */
      celebrate();
      return { success: true };
    } catch (err) {
      setAuthError(err.message);
      return { success: false, error: err.message };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // ── Google auth ────────────────────────────────────────────
  const googleAuth = useCallback(async ({ credential }) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const { token, user: me } = await apiGoogleAuth({ credential });
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(me));
      setUser(me);
      setIsLoggedIn(true);
      /* The sidebar does not exist yet — the bus parks this until the
         shell mounts after the redirect. */
      celebrate();
      return { success: true };
    } catch (err) {
      setAuthError(err.message);
      return { success: false, error: err.message };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // ── User logout ─────────────────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setIsLoggedIn(false);
  }, []);

  // ── Admin login ─────────────────────────────────────────────
  const adminLogin = useCallback(async ({ email, password }) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const { token } = await apiAdminLogin({ email, password });
      localStorage.setItem(ADMIN_TOKEN_KEY, token);
      setIsAdmin(true);
      return { success: true };
    } catch (err) {
      setAuthError(err.message);
      return { success: false, error: err.message };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // ── Admin logout ────────────────────────────────────────────
  const adminLogout = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setIsAdmin(false);
  }, []);

  // ── Update user in state after XP/streak changes ────────────
  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = {
    user,
    isLoggedIn,
    isAdmin,
    authLoading,
    authError,
    login,
    signup,
    verifySignup,
    googleAuth,
    logout,
    adminLogin,
    adminLogout,
    updateUser,
    setAuthError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ── Hook ───────────────────────────────────────────────────── */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
